import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import FixtureMundial from '../components/FixtureMundial/FixtureMundial';
import './Inicio.css';

const Loader = ({ texto = 'Cargando...' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '20px' }}>
    <div style={{ position: 'relative', width: '56px', height: '56px' }}>
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid #0f172a' }} />
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid transparent', borderTopColor: '#00f2ff', animation: 'spin 0.9s linear infinite' }} />
      <div style={{ position: 'absolute', inset: '8px', borderRadius: '50%', border: '2px solid transparent', borderTopColor: '#a855f7', animation: 'spin 1.4s linear infinite reverse' }} />
    </div>
    <p style={{ color: '#475569', fontSize: '0.78rem', letterSpacing: '2px', textTransform: 'uppercase', margin: 0 }}>{texto}</p>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);



const SkeletonPanel = () => {
  const msgs = ['Cargando torneos...', 'Buscando prodes...', 'Calculando posiciones...', 'Trayendo resultados...', 'Casi listo...'];
  const [msgIdx, setMsgIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setMsgIdx(i => (i + 1) % msgs.length), 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#00f2ff', animation: 'pulse 1s infinite' }} />
        <span style={{ color: '#cec8c8', fontSize: '1.2rem', letterSpacing: '1px' }}>{msgs[msgIdx]}</span>
      </div>
      {[1,2,3].map(i => (
        <div key={i} style={{ height: '36px', borderRadius: '8px', marginBottom: '4px',
          background: 'linear-gradient(90deg, #080d14 25%, #0f172a 50%, #080d14 75%)',
          backgroundSize: '200% 100%', animation: `shimmer 1.4s ${i*0.15}s infinite` }} />
      ))}
      <div style={{ height: '8px', width: '40%', background: '#0f1923', borderRadius: '4px', margin: '10px 0 6px' }} />
      {[1,2].map(i => (
        <div key={i} style={{ height: '52px', borderRadius: '8px', marginBottom: '4px',
          background: 'linear-gradient(90deg, #080d14 25%, #0f172a 50%, #080d14 75%)',
          backgroundSize: '200% 100%', animation: `shimmer 1.4s ${i*0.2}s infinite` }} />
      ))}
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>
    </>
  );
};

const Inicio = ({ session, draftResult, equipoActual, equipoLibre, formacionLibre, nickname, jornadaActiva = 1, capitanIndex = null }) => {
  const navigate = useNavigate();
  const [torneos, setTorneos] = useState([]);
  const [ultimaFecha, setUltimaFecha] = useState(null);
  const [topUltimaFecha, setTopUltimaFecha] = useState([]);
  const [miPosicion, setMiPosicion] = useState({});
  const [ventana, setVentana] = useState(null);
  const [prodeResumen, setProdeResumen] = useState([]);
  const [tiempoRestante, setTiempoRestante] = useState(null);
  const [equipoActivo, setEquipoActivo] = useState(-1);
  const [panelIzqTab, setPanelIzqTab] = useState('tactico');
  const [cardInfoIdx, setCardInfoIdx] = useState(0); // card visible en mobile (info inicial)
  const [generalAcumulado, setGeneralAcumulado] = useState({ tactico: 0, libre: 0 });
  const [misPuntosPorTipo, setMisPuntosPorTipo] = useState({ tactico: null, libre: null });
  const [miPuntajeJugador, setMiPuntajeJugador] = useState({}); // { jugador_id: pts }
  const [equipoConPuntos, setEquipoConPuntos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [cargandoEquipo, setCargandoEquipo] = useState(false);
  const [misMedallas, setMisMedallas] = useState(0);
  const [misJugadasPreparadas, setMisJugadasPreparadas] = useState(0);
  const TOTAL_JUGADAS_PREPARADAS = 8;

  // ⚠️ NO BORRAR: pre-cargar cache al montar para evitar parpadeo (antes del fetch)
  useEffect(() => {
    if (!session?.user?.id) return;
    const userId = session.user.id;
    try {
      const cached = JSON.parse(sessionStorage.getItem(`dtxi_inicio_${userId}`) || 'null');
      if (cached) {
        if (cached.torneos) setTorneos(cached.torneos);
        if (cached.ventana) setVentana(cached.ventana);
        // Solo restaurar equipo si la jornada del cache coincide con la actual
        // (evita mostrar equipo de jornada vieja cuando cambia la jornada)
        // Restaurar equipo del cache solo si tenemos datos almacenados
        if (cached.equipoConPuntos) {
          setEquipoConPuntos(cached.equipoConPuntos);
          if (cached.miPuntajeJugador) setMiPuntajeJugador(cached.miPuntajeJugador);
        }
      }
    } catch(e) {}
  }, [session?.user?.id]); // eslint-disable-line

  useEffect(() => {
    const cargar = async () => {
      if (!session?.user?.id) return;
      const userId = session.user.id;

      const [
        { data: misParticipaciones },
        { data: misProdes },
        { data: ventanas },
        { data: medallas },
      ] = await Promise.all([
        supabase.from('torneo_participantes').select('*, torneo:torneos(id, nombre, tipo)').eq('user_id', userId),
        supabase.from('prode_participantes').select('*, torneo:prode_torneos(id, nombre)').eq('user_id', userId),
        supabase.from('ventana_transferencias').select('*').eq('activa', true).order('numero_fecha', { ascending: false }).limit(1),
        supabase.from('medallas_usuario').select('tipo, emoji').eq('user_id', userId),
      ]);
      setVentana(ventanas?.[0] || null);
      setTorneos(misParticipaciones || []);
      if (medallas) {
        setMisMedallas(medallas.length);
        setMisJugadasPreparadas(medallas.filter(m => m.tipo === 'jugada_preparada' && m.emoji?.endsWith('.png')).length);
      }

      // Si la jornada activa > 1 y el equipo actual puede estar vacío (j nueva),
      // precargar el draft de j anterior para el widget de inicio
      const jornadaActiva = ventanas?.[0]?.numero_fecha || 1;
      const jornadaHistorico = jornadaActiva > 1 ? jornadaActiva - 1 : 1;

      // Si la jornada cambió respecto al cache, limpiar equipo cacheado para evitar parpadeo
      try {
        const cached = JSON.parse(sessionStorage.getItem(`dtxi_inicio_${userId}`) || 'null');
        if (cached?.jornadaHistorico && cached.jornadaHistorico !== jornadaHistorico) {
          setEquipoConPuntos(null);
          setMiPuntajeJugador({});
        }
      } catch(e) {}

      // Siempre cargar equipo histórico de la última jornada jugada
      let equipoConPuntosLocal = null;
      let miPuntajeJugadorLocal = {};
      const { data: draftAnterior } = await supabase.from('drafts')
        .select('equipo, capitan_index')
        .eq('user_id', userId).eq('jornada', jornadaHistorico).maybeSingle();
      if (draftAnterior?.equipo && Object.keys(draftAnterior.equipo).length > 0) {
        equipoConPuntosLocal = { equipo: draftAnterior.equipo, capitanIndex: draftAnterior.capitan_index };
        setEquipoConPuntos(equipoConPuntosLocal);
        const { data: partics } = await supabase.from('torneo_participantes')
          .select('torneo_id').eq('user_id', userId);
        const tIds = (partics || []).map(p => p.torneo_id);
        if (tIds.length) {
          const { data: fechasJ } = await supabase.from('torneo_fechas')
            .select('id').in('torneo_id', tIds).eq('numero_fecha', jornadaHistorico);
          const fIds = (fechasJ || []).map(f => f.id);
          if (fIds.length) {
            const { data: ptRows } = await supabase.from('torneo_puntos')
              .select('puntos_por_jugador, puntos').in('fecha_id', fIds)
              .eq('user_id', userId).order('puntos', { ascending: false }).limit(1);
            if (ptRows?.[0]?.puntos_por_jugador) {
              miPuntajeJugadorLocal = ptRows[0].puntos_por_jugador;
              setMiPuntajeJugador(miPuntajeJugadorLocal);
            }
          }
        }
      }

      if (misProdes?.length) {
        const resumen = await Promise.all(misProdes.map(async (pp) => {
          // Fecha activa
          const { data: fechaActiva } = await supabase.from('prode_fechas')
            .select('id').eq('torneo_id', pp.torneo.id).eq('activa', true).eq('cerrada', false).maybeSingle();

          // Posición del usuario en la tabla del prode
          const { data: tablaData } = await supabase.from('prode_participantes')
            .select('user_id, puntos_totales').eq('torneo_id', pp.torneo.id)
            .order('puntos_totales', { ascending: false });
          const tablaSorted = tablaData || [];
          const miPos = tablaSorted.findIndex(r => r.user_id === userId) + 1;
          const miPuntos = tablaSorted.find(r => r.user_id === userId)?.puntos_totales || 0;

          if (!fechaActiva) return { ...pp, pendientes: 0, hechos: 0, posicion: miPos, puntos: miPuntos };

          // Partidos de la fecha activa
          const { count: total } = await supabase.from('prode_partidos')
            .select('*', { count: 'exact', head: true })
            .eq('fecha_id', fechaActiva.id);

          // Pronósticos del usuario para esos partidos (por partido_id de esa fecha)
          const { data: partFecha } = await supabase.from('prode_partidos')
            .select('id').eq('fecha_id', fechaActiva.id);
          const partidoIds = (partFecha || []).map(p => p.id);
          let hechos = 0;
          if (partidoIds.length > 0) {
            const { count: h } = await supabase.from('prode_pronosticos')
              .select('*', { count: 'exact', head: true })
              .eq('torneo_id', pp.torneo.id)
              .eq('user_id', userId)
              .in('partido_id', partidoIds);
            hechos = h || 0;
          }
          return { ...pp, pendientes: total || 0, hechos, posicion: miPos, puntos: miPuntos };
        }));
        setProdeResumen(resumen);
      }

      if (misParticipaciones?.length) {
        const torneoIds = misParticipaciones.map(p => p.torneo.id);
        const { data: puntosData } = await supabase.from('torneo_puntos')
          .select('torneo_id, user_id, puntos').in('torneo_id', torneoIds);

        const posiciones = {};
        torneoIds.forEach(tid => {
          const pts = puntosData?.filter(p => p.torneo_id === tid) || [];
          const ranking = {};
          pts.forEach(p => { ranking[p.user_id] = (ranking[p.user_id] || 0) + p.puntos; });
          const sorted = Object.entries(ranking).sort((a, b) => b[1] - a[1]);
          const pos = sorted.findIndex(([uid]) => uid === userId) + 1;
          posiciones[tid] = { pos: pos || '—', puntos: ranking[userId] || 0 };
        });
        setMiPosicion(posiciones);

        const tacticoIds = misParticipaciones.filter(p => p.torneo.tipo === 'tactico').map(p => p.torneo.id);
        const libreIds   = misParticipaciones.filter(p => p.torneo.tipo === 'libre').map(p => p.torneo.id);
        const sumTipo = (ids) => (puntosData || [])
          .filter(p => ids.includes(p.torneo_id) && p.user_id === userId)
          .reduce((acc, p) => acc + p.puntos, 0);
        setGeneralAcumulado({ tactico: sumTipo(tacticoIds), libre: sumTipo(libreIds) });

        // Última fecha táctico — buscar en TODOS los torneos tácticos
        const tacticos = misParticipaciones.filter(p => p.torneo.tipo === 'tactico');
        const libre   = misParticipaciones.find(p => p.torneo.tipo === 'libre');

        if (tacticos.length) {
          const todosIds = tacticos.map(p => p.torneo.id);
          // Buscar la última fecha del torneo que tenga puntos cargados
          const { data: todasFechasTac } = await supabase.from('torneo_fechas')
            .select('*').in('torneo_id', todosIds)
            .order('numero_fecha', { ascending: false });
          // Encontrar la última fecha con puntos del usuario
          let miRowTac = null;
          let fechaTac = null;
          for (const fecha of (todasFechasTac || [])) {
            const { data: rows } = await supabase.from('torneo_puntos')
              .select('puntos, equipo, capitan_index, puntos_por_jugador, fecha_id')
              .eq('user_id', userId).eq('fecha_id', fecha.id).gt('puntos', 0).limit(1);
            if (rows?.[0]) { miRowTac = rows[0]; fechaTac = fecha; break; }
          }
          if (miRowTac && fechaTac) {
            setUltimaFecha({ ...fechaTac, torneo: tacticos[0]?.torneo });
            setMisPuntosPorTipo(prev => ({ ...prev, tactico: { puntos: miRowTac.puntos, numeroFecha: fechaTac.numero_fecha } }));
            if (miRowTac.equipo && Object.keys(miRowTac.equipo).length > 0) {
              setEquipoConPuntos({ equipo: miRowTac.equipo, capitanIndex: miRowTac.capitan_index });
              setMiPuntajeJugador(miRowTac.puntos_por_jugador || {});
            }
            // Top 3 de esa fecha
            const { data: puntosFecha } = await supabase.from('torneo_puntos')
              .select('user_id, puntos').eq('fecha_id', fechaTac.id).order('puntos', { ascending: false }).limit(3);
            if (puntosFecha?.length) {
              const { data: participantes } = await supabase.from('perfiles')
                .select('id, nickname').in('id', (puntosFecha||[]).map(p => p.user_id));
              const mapaNames = {};
              participantes?.forEach(p => { mapaNames[p.id] = p.nickname; });
              setTopUltimaFecha(puntosFecha.map(p => ({ ...p, nombre: mapaNames[p.user_id] || '?' })));
            }
          }
        }

        // Última fecha libre
        if (libre) {
          const { data: fechasLibTodas } = await supabase.from('torneo_fechas').select('*')
            .eq('torneo_id', libre.torneo.id).order('numero_fecha', { ascending: false });
          let fechaLibConDatos = null;
          if (fechasLibTodas?.length) {
            for (const f of fechasLibTodas) {
              const { data: check } = await supabase.from('torneo_puntos')
                .select('id').eq('fecha_id', f.id).limit(1);
              if (check?.length) { fechaLibConDatos = f; break; }
            }
          }
          if (fechaLibConDatos) {
            const { data: miPtsLib } = await supabase.from('torneo_puntos')
              .select('puntos').eq('fecha_id', fechaLibConDatos.id).eq('user_id', userId).maybeSingle();
            setMisPuntosPorTipo(prev => ({ ...prev, libre: miPtsLib?.puntos != null ? { puntos: miPtsLib.puntos, numeroFecha: fechaLibConDatos.numero_fecha } : null }));
          }
        }
      }

      // ⚠️ NO BORRAR: guardar en cache para carga instantánea al volver
      try {
        sessionStorage.setItem(`dtxi_inicio_${userId}`, JSON.stringify({
          torneos: misParticipaciones || [],
          ventana: ventanas?.[0] || null,
          equipoConPuntos: equipoConPuntosLocal || null,
          miPuntajeJugador: miPuntajeJugadorLocal || {},
          jornadaHistorico,
        }));
      } catch(e) {}

      setCargando(false); // todo cargado — mostrar de una
    };
    cargar();
  }, [session]);

  // ⚠️ NO BORRAR: cargar puntaje desde torneo_puntos que ya tiene capitán calculado
  useEffect(() => {
    const cargarPuntosJugadores = async () => {
      const userId = session?.user?.id;
      if (!userId) return;

      const tipo = equipoActivo === 0 ? 'tactico' : 'libre';

      // Buscar torneos del tipo correcto donde participa el usuario
      const { data: participaciones } = await supabase
        .from('torneo_participantes')
        .select('torneo_id, torneo:torneos(tipo)')
        .eq('user_id', userId);

      const torneoIds = (participaciones || []).filter(p => p.torneo?.tipo === tipo).map(p => p.torneo_id);
      if (!torneoIds.length) {
        setMiPuntajeJugador({});
        setEquipoConPuntos(null);
        return;
      }

      // Buscar la última fecha con puntos en TODOS los torneos del tipo
      const { data: todasFechas } = await supabase
        .from('torneo_fechas').select('id, numero_fecha')
        .in('torneo_id', torneoIds).order('numero_fecha', { ascending: false });

      // Buscar la última fecha con puntos del usuario (por numero_fecha desc, no por puntos desc)
      let miRow = null;
      for (const fecha of (todasFechas || [])) {
        const { data: rows } = await supabase.from('torneo_puntos')
          .select('puntos_por_jugador, equipo, capitan_index, puntos')
          .eq('fecha_id', fecha.id)
          .eq('user_id', userId)
          .gt('puntos', 0).limit(1);
        if (rows?.[0]) { miRow = rows[0]; break; }
      }

      if (!miRow) {
        setMiPuntajeJugador({});
        setEquipoConPuntos(null);
        return;
      }

      setMiPuntajeJugador(miRow?.puntos_por_jugador || {});
      // Guardar el equipo histórico para mostrarlo cuando el equipo actual es de otra jornada
      if (miRow?.equipo && Object.keys(miRow.equipo).length > 0) {
        setEquipoConPuntos({ equipo: miRow.equipo, capitanIndex: miRow.capitan_index });
      } else {
        setEquipoConPuntos(null);
      }
    };

    cargarPuntosJugadores();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipoActivo, equipoActual, equipoLibre]);

  useEffect(() => {
    if (!ventana?.cierre) return;
    const calcular = () => {
      const diff = new Date(ventana.cierre) - new Date();
      if (diff <= 0) { setTiempoRestante(null); return; }
      setTiempoRestante({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    calcular();
    const t = setInterval(calcular, 1000);
    return () => clearInterval(t);
  }, [ventana]);

  // Mostrar siempre el equipo histórico (última jornada jugada con puntos)
  // equipoConPuntos = draft de jornadaActiva-1, que ya puntuó
  // Mostrar siempre el equipo de la ÚLTIMA jornada jugada (equipoConPuntos)
  const tacticoJugadores = (() => {
    if (equipoActivo === 1) return equipoActual || {};
    if (equipoConPuntos?.equipo) return equipoConPuntos.equipo;
    return equipoActual || {};
  })();
  const tacticoCapitan = (() => {
    if (equipoActivo === 1) return capitanIndex;
    if (equipoConPuntos?.equipo) return equipoConPuntos.capitanIndex;
    return capitanIndex;
  })();

  const EQUIPOS = [
    { titulo: 'Táctico', formacion: draftResult?.formacion || '4-3-3', presupuesto: draftResult?.presupuesto || 0, jugadores: tacticoJugadores, link: '/equipo-tactico', color: '#00f2ff', icon: '', restricciones: draftResult?.restricciones || [] },
    { titulo: 'Libre',   formacion: formacionLibre || '4-3-3',          presupuesto: 60000000,                      jugadores: equipoLibre || {},  link: '/equipo-libre',   color: '#a855f7', icon: '', restricciones: [] },
  ];

  const eq = EQUIPOS[equipoActivo] || EQUIPOS[0];
  const titularesConSlot = Object.entries(eq.jugadores).filter(([k]) => Number(k) <= 10 && eq.jugadores[k]);
  const titulares = titularesConSlot.map(([, j]) => j).filter(Boolean);
  const capitanSlot = equipoActivo === 0 ? tacticoCapitan : null;
  const suplentes = Object.entries(eq.jugadores).filter(([k]) => Number(k) >= 20).map(([, j]) => j).filter(Boolean);
  const parsearPresupuesto = (p) => {
    if (!p) return 0;
    if (typeof p === 'number') return p;
    const s = String(p).trim();
    // Formato "$40M" o "40M" → multiplicar por 1M
    if (s.toUpperCase().includes('M')) return parseInt(s.replace(/[^0-9]/g, '')) * 1000000;
    // Formato "45500000" → ya es el número completo
    const n = parseInt(s.replace(/[^0-9]/g, ''));
    return isNaN(n) ? 0 : n;
  };
  const presNum = parsearPresupuesto(eq.presupuesto);
  const gastado = titulares.reduce((a, j) => a + (j?.precio || 0), 0);
  const disponible = presNum - gastado;
  const completo = titulares.length === 11;
  const restriccionesActivas = eq.restricciones.filter(r => !r.anulada);
  const POS_ORDER = ['DEL', 'VOL', 'DEF', 'ARQ'];
  const POS_COLOR = { DEL: '#ef4444', VOL: '#3b82f6', DEF: '#22c55e', ARQ: '#f59e0b' };
  const porLinea = {
    DEL: titulares.filter(j => (j.posicion?.sigla || j.posicion) === 'DEL'),
    VOL: titulares.filter(j => (j.posicion?.sigla || j.posicion) === 'VOL'),
    DEF: titulares.filter(j => (j.posicion?.sigla || j.posicion) === 'DEF'),
    ARQ: titulares.filter(j => (j.posicion?.sigla || j.posicion) === 'ARQ'),
  };
  const lineas = POS_ORDER.filter(k => porLinea[k].length > 0);
  const puntajeJugador = miPuntajeJugador; // puntos_por_jugador del usuario actual, cargado directamente
  const MEDALLAS = ['🥇', '🥈', '🥉'];
  const labelColor = '#64748b';

  return (
    <div className="inicio-root">

      {/* RELOJ */}
      <div className="inicio-reloj">
        <p style={{ color: labelColor, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '2px', margin: 0 }}>
          Tiempo para que arranque la fecha
        </p>
        {tiempoRestante ? (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {[{ val: tiempoRestante.d, label: 'D' }, { val: tiempoRestante.h, label: 'H' }, { val: tiempoRestante.m, label: 'M' }, { val: tiempoRestante.s, label: 'S' }].map(({ val, label }, idx) => (
              <React.Fragment key={label}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div className="reloj-digit">
                    {String(val).padStart(2, '0')}
                  </div>
                  <p style={{ color: '#2d4a5a', fontSize: '0.55rem', margin: '3px 0 0', textTransform: 'uppercase', letterSpacing: '2px' }}>{label}</p>
                </div>
                {idx < 3 && <span style={{ color: '#00f2ff33', fontSize: '1.8rem', marginBottom: '16px', fontFamily: 'monospace' }}>:</span>}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <p style={{ color: labelColor, fontSize: '0.9rem', margin: 0 }}>Ventana cerrada</p>
        )}
      </div>

      {/* 3 COLUMNAS */}
      <div className="inicio-grid">

        {/* IZQUIERDA */}
        <div className="inicio-panel-izq">

          {cargando ? <SkeletonPanel /> : (
            <>
          {/* TABS torneos */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', flexShrink: 0 }}>
            {[
              { key: 'tactico', label: 'Táctico', color: '#00f2ff' },
              { key: 'libre',   label: 'Libre', color: '#a855f7' },
              { key: 'prode',   label: 'Prode', color: '#f59e0b' },
            ].map(({ key, label, color }) => (
              <button key={key} onClick={() => setPanelIzqTab(key)} style={{
                flex: 1, padding: '5px 4px', borderRadius: '6px', border: `1px solid ${panelIzqTab === key ? color : '#1e293b'}`,
                background: panelIzqTab === key ? color + '15' : 'transparent',
                color: panelIzqTab === key ? color : '#475569',
                cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold', transition: '0.2s',
              }}>{label}</button>
            ))}
          </div>

          {/* Tab Táctico */}
          {panelIzqTab === 'tactico' && torneos.filter(t => t.torneo.tipo === 'tactico').map((t, i) => {
            const pos = miPosicion[t.torneo.id];
            return (
              <div key={i} onClick={() => navigate('/torneo/' + t.torneo.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 10px', background: '#080d14', borderRadius: '8px', border: '1px solid #1e293b', cursor: 'pointer', transition: '0.2s', flexShrink: 0 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#00f2ff'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e293b'; }}
              >
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#00f2ff', flexShrink: 0 }} />
                <span style={{ color: '#e2e8f0', fontSize: '0.9rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.torneo.nombre}</span>
                {pos && <span style={{ color: pos.pos === 1 ? '#f59e0b' : '#64748b', fontWeight: 'bold', fontSize: '0.82rem' }}>#{pos.pos}</span>}
              </div>
            );
          })}
          {panelIzqTab === 'tactico' && torneos.filter(t => t.torneo.tipo === 'tactico').length === 0 && (
            <p style={{ color: '#334155', fontSize: '0.8rem', margin: '8px 0' }}>Sin torneos tácticos</p>
          )}

          {/* Tab Libre */}
          {panelIzqTab === 'libre' && torneos.filter(t => t.torneo.tipo === 'libre').map((t, i) => {
            const pos = miPosicion[t.torneo.id];
            return (
              <div key={i} onClick={() => navigate('/torneo/' + t.torneo.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 10px', background: '#080d14', borderRadius: '8px', border: '1px solid #1e293b', cursor: 'pointer', transition: '0.2s', flexShrink: 0 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#a855f7'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e293b'; }}
              >
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#a855f7', flexShrink: 0 }} />
                <span style={{ color: '#e2e8f0', fontSize: '0.9rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.torneo.nombre}</span>
                {pos && <span style={{ color: pos.pos === 1 ? '#f59e0b' : '#64748b', fontWeight: 'bold', fontSize: '0.82rem' }}>#{pos.pos}</span>}
              </div>
            );
          })}
          {panelIzqTab === 'libre' && torneos.filter(t => t.torneo.tipo === 'libre').length === 0 && (
            <p style={{ color: '#334155', fontSize: '0.8rem', margin: '8px 0' }}>Sin torneos libres</p>
          )}

          {/* Tab Prode */}
          {panelIzqTab === 'prode' && prodeResumen.length === 0 && (
            <p style={{ color: '#334155', fontSize: '0.8rem', margin: '8px 0' }}>Sin torneos de prode</p>
          )}
          {panelIzqTab === 'prode' && prodeResumen.map((p, i) => {
            const faltantes = p.pendientes - p.hechos;
            const completo  = p.pendientes > 0 && faltantes <= 0;
            const pct = p.pendientes > 0 ? Math.round((p.hechos / p.pendientes) * 100) : 0;
            return (
              <div key={i} onClick={() => navigate('/prode')}
                style={{ padding: '8px 10px', background: '#080d14', borderRadius: '8px', border: '1px solid #1e293b', cursor: 'pointer', flexShrink: 0, transition: '0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b44'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e293b'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ color: '#e2e8f0', fontSize: '0.82rem' }}>{p.torneo.nombre}</span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {p.posicion > 0 && <span style={{ color: p.posicion === 1 ? '#f59e0b' : '#475569', fontSize: '0.7rem', fontWeight: 'bold' }}>#{p.posicion}</span>}
                    {p.puntos > 0 && <span style={{ color: '#f59e0b', fontSize: '0.72rem', fontWeight: 'bold' }}>{p.puntos}pts</span>}
                  </div>
                </div>
                {p.pendientes > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                      <span style={{ color: '#475569', fontSize: '0.7rem' }}>{p.hechos}/{p.pendientes} pronósticos</span>
                      <span style={{ color: completo ? '#22c55e' : '#f59e0b', fontSize: '0.7rem', fontWeight: 'bold' }}>{completo ? '✓ listo' : faltantes + ' falt.'}</span>
                    </div>
                    <div style={{ height: '3px', background: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: pct + '%', height: '100%', background: completo ? '#22c55e' : '#f59e0b', transition: '0.3s' }} />
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {/* Última fecha */}
          {ultimaFecha && (
            <div className="inicio-ultima-fecha">
              <p style={{ color: labelColor, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '6px 0 2px', flexShrink: 0 }}>
                {'Fecha ' + ultimaFecha.numero_fecha + ' — ' + (ultimaFecha.torneo?.nombre || '')}
              </p>
              {topUltimaFecha.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 9px', background: p.user_id === session.user.id ? '#00f2ff08' : '#080d14', borderRadius: '7px', border: '1px solid ' + (p.user_id === session.user.id ? '#00f2ff22' : '#1e293b'), flexShrink: 0 }}>
                  <span style={{ fontSize: '0.95rem' }}>{MEDALLAS[i]}</span>
                  <span style={{ color: p.user_id === session.user.id ? '#00f2ff' : '#e2e8f0', fontSize: '0.9rem', flex: 1 }}>{p.nombre}</span>
                  <span style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '0.9rem' }}>{p.puntos}</span>
                </div>
              ))}
            </div>
          )}

          <p style={{ color: '#f8920e', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '6px 0 2px', flexShrink: 0 }}>General acumulado</p>
          <div className="inicio-general-row">
            {[{ label: 'Táctico', val: generalAcumulado.tactico, color: '#00f2ff', bg: '#00f2ff18', border: '#00f2ff55' }, { label: 'Libre', val: generalAcumulado.libre, color: '#a855f7', bg: '#a855f718', border: '#a855f755' }].map(({ label, val, color, bg, border }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', background: bg, borderRadius: '8px', border: `1px solid ${border}`, flexShrink: 0 }}>
                <span style={{ color: color, fontSize: '0.75rem', fontWeight: 'bold', flex: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
                <span style={{ color: color, fontSize: '1.3rem', fontWeight: '900', textShadow: `0 0 12px ${color}` }}>{val}</span>
                <span style={{ color: color, fontSize: '0.65rem', opacity: 0.7 }}>pts</span>
              </div>
            ))}
          </div>
            </>
          )}
        </div>

        {/* CENTRO */}
        <div className="inicio-panel-centro">

          {/* TABS: Solo Información inicial (Táctico y Libre temporalmente desactivados) */}
          <div className="inicio-tabs-equipo" style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            <button onClick={() => setEquipoActivo(-1)} style={{
              flex: 1, padding: '7px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem',
              border: `1px solid #00f2ff`,
              background: '#00f2ff15',
              color: '#00f2ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: '0.2s',
            }}>Información inicial</button>
            {/* TAB TÁCTICO — comentado temporalmente
            ...
            */}
            {/* TAB LIBRE — comentado temporalmente
            ...
            */}
          </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'linear-gradient(135deg,#080d14,#0a0f19)', border: '1px solid ' + eq.color + '22', borderRadius: '10px', overflow: 'hidden', minHeight: 0 }}>

            {cargandoEquipo ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', border: '3px solid #1e293b', borderTop: '3px solid #00f2ff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <p style={{ color: '#475569', fontSize: '0.85rem' }}>Cargando equipo...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : equipoActivo === -1 ? (
              /* SIN EQUIPO — 6 cards de bienvenida, en mobile se navegan de a una */
              (() => {
                const cards = [
                  /* 1 — Puntos */
                  (() => {
                    const ids = Object.keys(miPosicion);
                    const mejorPos = ids.length > 0 ? Math.min(...ids.map(id => parseInt(miPosicion[id]?.pos) || 99).filter(p => p < 99)) : null;
                    return (
                      <div key="pts" onClick={() => navigate('/ranked')}  onMouseEnter={e => e.currentTarget.style.borderColor = '#22c55e66'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = '#1e293b'} style={{cursor: 'pointer', pointerEvents: 'all', transition: 'border-color 0.2s',  background: '#0a0f19', border: `1px solid ${eq.color}22`, borderRadius: '10px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', height: '100%' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ color: '#15dab9', fontSize: '1.12rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>Por los puntos</p>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.0rem', marginBottom: '5px' }}>
                            <span style={{ color: '#e2e8f0' }}>Modo 1vs1 competitivo: sumá los puntos necesarios para ascender a otra división</span>
                          </div>
                          {mejorPos && mejorPos < 99 ? (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.0rem' }}>
                              <span style={{ color: '#cbd5e1' }}>Ranking</span>
                              <span style={{ color: '#e2e8f0' }}>#{mejorPos}</span>
                            </div>
                          ) : (
                            <p style={{ color: '#f1b307', fontSize: '1.2rem', margin: '4px 0 0' }}>División D</p>
                          )}
                        </div>
                      </div>
                    );
                  })(),
                  /* 2 — Última fecha */
                  <div key="fecha" onClick={() => navigate('/equipo-tactico')}  onMouseEnter={e => e.currentTarget.style.borderColor = '#00f2ff66'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = '#1e293b'} style={{ cursor: 'pointer', pointerEvents: 'all', transition: 'border-color 0.2s', background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '10px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', height: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: '#15dab9', fontSize: '1.12rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>{ultimaFecha ? `Fecha ${ultimaFecha.numero_fecha} — ${ultimaFecha.torneo?.nombre || 'Táctico'}` : 'Última fecha'}</p>
                      {topUltimaFecha.length > 0 ? topUltimaFecha.slice(0, 3).map((p, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.0rem', marginBottom: '5px', gap: '10px' }}>
                          <span style={{ color: i === 0 ? '#f59e0b' : '#a8b8ce', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>#{i + 1} {p.nombre}</span>
                          <span style={{ color: '#94a3b8', fontWeight: i === 0 ? 'bold' : 'normal', flexShrink: 0 }}>{p.puntos} pts</span>
                        </div>
                      )) : <p style={{ color: '#a7adb4', fontSize: '1.2rem', margin: 0, fontStyle: 'italic' }}>Todavía no hay fechas jugadas</p>}
                    </div>
                  </div>,
                  /* 3 — Palmarés */
                  <div key="palmares" onClick={() => navigate('/palmares')}  onMouseEnter={e => e.currentTarget.style.borderColor = '#f59e0b66'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = '#1e293b'} style={{cursor: 'pointer', pointerEvents: 'all', transition: 'border-color 0.2s',  background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '10px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', height: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: '#15dab9', fontSize: '1.12rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>Palmarés</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.0rem', marginBottom: '5px' }}><span style={{ color: '#e9cc12' }}>Medallas</span><span style={{ color: '#e2e8f0' }}>0 desbloqueadas</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.0rem', marginBottom: '5px' }}><span style={{ color: '#e9cc12' }}>Jugadas preparadas</span><span style={{ color: '#94a3b8' }}>0 / 8</span></div>
                      <p style={{ color: '#a7adb4', fontSize: '1.0rem', margin: '6px 0 0', fontStyle: 'italic' }}>Ganá fechas para desbloquear logros</p>
                    </div>
                  </div>,
                  /* 4 — Ruletas */
                  <div key="ruletas" onClick={() => navigate('/equipo-tactico')}  onMouseEnter={e => e.currentTarget.style.borderColor = '#f43f5e66'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = '#1e293b'} style={{ cursor: 'pointer', pointerEvents: 'all', transition: 'border-color 0.2s',background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '10px', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '10px', height: '100%' }}>
                    <p style={{ color: '#15dab9', fontSize: '1.12rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Las ruletas</p>
                    <p style={{ color: '#e2e8f0', fontSize: '1.0rem', lineHeight: 1.5, margin: 0 }}>Al inicio de cada fecha girás 4 ruletas: <span style={{ color: '#f43f5e', fontWeight: 'bold' }}>presupuesto</span>, <span style={{ color: '#f43f5e', fontWeight: 'bold' }}>formación</span>, <span style={{ color: '#f43f5e', fontWeight: 'bold' }}>cantidad</span> y <span style={{ color: '#f43f5e', fontWeight: 'bold' }}>tipo</span> de restricciones.</p>
                    <p style={{ color: '#a8b8ce', fontSize: '1.0rem', margin: 0, lineHeight: 1.4 }}>Las restricciones son condiciones que tu equipo debe cumplir — si no las cumplís, no podés confirmar.</p>
                  </div>,
                  /* 5 — Clan */
                  <div key="clan" onClick={() => navigate('/clan')}  onMouseEnter={e => e.currentTarget.style.borderColor = '#a78bfa66'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = '#1e293b'} style={{ cursor: 'pointer', pointerEvents: 'all', transition: 'border-color 0.2s', background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '10px', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '10px', height: '100%' }}>
                    <p style={{ color: '#15dab9', fontSize: '1.12rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>El club</p>
                    <p style={{ color: '#e2e8f0', fontSize: '1.0rem', lineHeight: 1.5, margin: 0 }}>Uníte a un club para competir en equipo: <span style={{ color: '#a78bfa', fontWeight: 'bold' }}>Desafíos 5v5</span>, <span style={{ color: '#a78bfa', fontWeight: 'bold' }}>retos grupales</span> y torneos exclusivos.</p>
                    <p style={{ color: '#a8b8ce', fontSize: '1.0rem', margin: 0, lineHeight: 1.4 }}>Las guerras son por matchmaking automático.</p>
                  </div>,
                  /* 6 — Torneos */
                  <div key="torneos" onClick={() => navigate('/torneo-tactico')} onMouseEnter={e => e.currentTarget.style.borderColor = '#c302d466'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = '#1e293b'}
                    style={{ cursor: 'pointer', pointerEvents: 'all', transition: 'border-color 0.2s', background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '10px', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '10px', height: '100%' }}>
                    <p style={{ color: '#15dab9', fontSize: '1.12rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Torneos y tablero</p>
                    <p style={{ color: '#e2e8f0', fontSize: '1.0rem', lineHeight: 1.5, margin: 0 }}>Los torneos tácticos tienen un <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>tablero estilo juego de mesa</span> de 26 casilleros. Tirás el dado y podés caer en bonus, comodines o penalizaciones.</p>
                    <p style={{ color: '#a8b8ce', fontSize: '1.0rem', margin: 0, lineHeight: 1.4 }}>Los comodines se guardan y podés usarlos para cambiar restricciones o sabotear rivales.</p>
                  </div>,
                ];
                
                return (
                  <>
                    {/* Desktop: grid 3x2 original */}
                    <div className="inicio-info-grid" style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', flex: 1, alignContent: 'center' }}>
                      {cards}
                    </div>
                    {/* Mobile: una card a la vez con flechas */}
                    <div className="inicio-info-mobile">
                      <div className="inicio-info-nav">
                        <button className="inicio-info-btn" onClick={() => setCardInfoIdx(i => Math.max(0, i - 1))} disabled={cardInfoIdx === 0}>◀</button>
                        <span className="inicio-info-pag">{cardInfoIdx + 1} / {cards.length}</span>
                        <button className="inicio-info-btn" onClick={() => setCardInfoIdx(i => Math.min(cards.length - 1, i + 1))} disabled={cardInfoIdx === cards.length - 1}>▶</button>
                      </div>
                      <div className="inicio-info-card-wrap">
                        {cards[cardInfoIdx]}
                      </div>
                    </div>
                  </>
                );
              })()
            ) : (
              <>
            <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

              {/* CANCHA — círculos más abajo, sin iniciales, área simplificada */}
              <div className="inicio-cancha-svg" style={{ width: '185px', flexShrink: 0, padding: '10px', borderRight: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg viewBox="0 0 100 140" style={{ width: '100%', height: '100%', maxHeight: '270px' }}>
                  <defs>
                    <linearGradient id="gc-inicio" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#071a0e" />
                      <stop offset="100%" stopColor="#091f12" />
                    </linearGradient>
                  </defs>
                  <rect width="100" height="140" rx="4" fill="url(#gc-inicio)" />
                  {[0, 1, 2, 3, 4, 5, 6].map(idx => (
                    <rect key={idx} x="1" y={1 + idx * 19.7} width="98" height="9.85" fill={idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'} />
                  ))}
                  {/* Borde */}
                  <rect x="1" y="1" width="98" height="138" rx="3" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.8" />
                  {/* Línea media */}
                  <line x1="1" y1="70" x2="99" y2="70" stroke="rgba(255,255,255,0.12)" strokeWidth="0.7" />
                  {/* Círculo central */}
                  <circle cx="50" cy="70" r="13" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.7" />
                  <circle cx="50" cy="70" r="1.5" fill="rgba(255,255,255,0.2)" />
                  {cargandoEquipo ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', border: '3px solid #1e293b', borderTop: '3px solid #00f2ff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <p style={{ color: '#475569', fontSize: '0.85rem' }}>Cargando equipo...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : equipoActivo === -1 ? (
                    <text x="50" y="72" textAnchor="middle" fill="rgba(255,255,255,0.07)" fontSize="5.5">Sin equipo</text>
                  ) : lineas.map((pos, li) => {
                    const linea = porLinea[pos];
                    const totalLineas = lineas.length;
                    // Separación vertical generosa: empieza en 20, termina en 125
                    const y = 20 + li * (105 / (totalLineas - 1 || 1));
                    const pc = POS_COLOR[pos] || eq.color;
                    return linea.map((jug, pi) => {
                      const x = 8 + (84 / (linea.length + 1)) * (pi + 1);
                      return (
                        <g key={li + '-' + pi}>
                          <circle cx={x} cy={y} r="6.5" fill={pc} fillOpacity="0.2" />
                          <circle cx={x} cy={y} r="6.5" fill="none" stroke={pc} strokeWidth="1.2" strokeOpacity="0.9" />
                        </g>
                      );
                    });
                  })}
                </svg>
              </div>

              {/* JUGADORES */}
              <div className="inicio-jugadores-col" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                <div style={{ flex: 1, padding: '10px 14px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                  <p style={{ color: labelColor, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 6px', fontWeight: 'bold', flexShrink: 0 }}>
                    Jugadores
                    {Object.keys(puntajeJugador).length > 0 && (
                      <span style={{ color: '#f59e0b', marginLeft: '6px', fontWeight: 'normal' }}>
                        {'· pts Fecha ' + (ultimaFecha ? ultimaFecha.numero_fecha : '')}
                      </span>
                    )}
                  </p>
                  {titulares.length === 0 ? (
                    <span style={{ color: '#d6d8da', fontSize: '1.3rem' }}>Sin jugadores aún</span>
                  ) : titularesConSlot.map(([slot, j], ji) => {
                    if (!j) return null;
                    const pts = puntajeJugador[j.id];
                    const paisSigla = j.seleccion ? j.seleccion.slice(0, 3).toUpperCase() : (j.nacionalidad ? j.nacionalidad.slice(0, 3).toUpperCase() : '—');
                    const posigla = j.posicion?.sigla || j.posicion || '—';
                    const esCapitan = capitanSlot !== null && Number(slot) === capitanSlot;
                    return (
                      <div key={ji} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0', borderBottom: ji < titularesConSlot.length - 1 ? '1px solid #0f172a' : 'none' }}>
                        <span style={{ color: '#f8920e', fontSize: '0.99rem', width: '15px', textAlign: 'right', flexShrink: 0 }}>{ji + 1}</span>
                        <span style={{ color: '#d7dce2', fontSize: '1.2rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.apellido}</span>
                        {esCapitan && <span style={{ color: '#f59e0b', fontSize: '0.6rem', fontWeight: 'bold', border: '1px solid #f59e0b', borderRadius: '3px', padding: '0 3px', flexShrink: 0 }}>C</span>}
                        <span style={{ color: '#f8920e', fontSize: '1.0rem', flexShrink: 0 }}>{posigla}</span>
                        <span style={{ color: '#d7dce2', fontSize: '1.0rem', flexShrink: 0 }}>{paisSigla}</span>
                        <span style={{ color: pts != null ? '#00f2ff' : '#334155', fontSize: '1.12rem', fontWeight: 'bold', flexShrink: 0, minWidth: '26px', textAlign: 'right' }}>{pts != null ? pts : '—'}</span>
                      </div>
                    );
                  })}

                  {/* SUPLENTES */}
                  {suplentes.length > 0 && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '6px 0 4px', flexShrink: 0 }}>
                        <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, #b3b8c0)' }} />
                        <span style={{ color: '#d7dce2', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '1.5px', flexShrink: 0 }}>Suplentes</span>
                        <div style={{ flex: 1, height: '1px', background: 'linear-gradient(270deg, transparent, #b3b8c0)' }} />
                      </div>
                      {suplentes.map((j, ji) => {
                        if (!j) return null;
                        const pts = puntajeJugador[j.id];
                        const paisSigla = j.seleccion ? j.seleccion.slice(0, 3).toUpperCase() : (j.nacionalidad ? j.nacionalidad.slice(0, 3).toUpperCase() : '—');
                        const posigla = j.posicion?.sigla || j.posicion || '—';
                        return (
                          <div key={ji} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0', borderBottom: ji < suplentes.length - 1 ? '1px solid #0a0f19' : 'none' }}>
                            <span style={{ color: '#f8920e', fontSize: '0.99rem', width: '15px', textAlign: 'right', flexShrink: 0 }}>S{ji + 1}</span>
                            <span style={{ color: '#d7dce2', fontSize: '1.2rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.apellido}</span>
                            <span style={{ color: '#f8920e', fontSize: '1.0rem', flexShrink: 0 }}>{posigla}</span>
                            <span style={{ color: '#a8b8ce', fontSize: '1.0rem', flexShrink: 0 }}>{paisSigla}</span>
                            <span style={{ color: pts != null ? '#00f2ff' : '#334155', fontSize: '1.12rem', fontWeight: 'bold', flexShrink: 0, minWidth: '26px', textAlign: 'right' }}>{pts != null ? pts : '—'}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>

                {/* MIS PUNTOS — calculado dinámicamente sumando puntajeJugador */}
                {(() => {
                  const totalPts = Object.values(puntajeJugador).reduce((acc, v) => acc + (v || 0), 0);
                  if (totalPts === 0) return null;
                  const tipo = equipoActivo === 0 ? 'tactico' : 'libre';
                  const dato = misPuntosPorTipo[tipo];
                  return (
                    <div style={{ padding: '8px 18px 14px', display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
                      <span style={{ color: labelColor, fontSize: '1.0rem', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                        {dato ? 'Fecha ' + dato.numeroFecha : 'Fecha actual'}
                      </span>
                      <span style={{ color: eq.color, fontSize: '2.8rem', fontWeight: 'bold', lineHeight: 1, textShadow: '0 0 20px ' + eq.color + 'cc, 0 0 40px ' + eq.color + '44' }}>
                        {totalPts}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* RESTRICCIONES */}
            {restriccionesActivas.length > 0 && (
              <div style={{ borderTop: '1px solid #1e293b', padding: '7px 14px', flexShrink: 0 }}>
                <p style={{ color: '#f8920e', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 5px' }}>Restricciones</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {restriccionesActivas.map((r, ri) => (
                    <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 9px', background: r.cumplida ? '#22c55e10' : eq.color + '0e', borderRadius: '6px', border: '1px solid ' + (r.cumplida ? '#22c55e33' : eq.color + '2a') }}>
                      <span style={{ color: r.cumplida ? '#22c55e' : eq.color, fontSize: '0.62rem' }}>{r.cumplida ? '✓' : '●'}</span>
                      <span style={{ color: r.cumplida ? '#22c55e88' : eq.color + 'bb', fontSize: '1.0rem' }}>{r.texto}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </div>

        {/* DERECHA: fixture */}
        <div className="inicio-panel-der">
          <FixtureMundial />
        </div>

      </div>
    </div>
  );
};

export default Inicio;